import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { Sparkles, TrendingDown, Calendar, ChevronRight, AlertCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { inr } from "@/lib/format";

const DEFAULTS = {
  salary_gross: 1200000, hra_received: 200000, rent_paid: 240000, metro_city: true,
  capital_gains_short: 0, capital_gains_long: 0, business_income: 0, interest_income: 12000,
  sec_80c: 150000, sec_80d: 25000, sec_80ccd_1b: 50000, home_loan_interest: 0,
};

const SUPPORTED_AYS = ["2025-26", "2026-27"];

export default function TaxCalculator() {
  const { user } = useAuth();
  const [income, setIncome] = useState(DEFAULTS);
  const [assessmentYear, setAssessmentYear] = useState("2026-27");
  const [senior, setSenior] = useState(false);
  const [superSenior, setSuperSenior] = useState(false);
  const [result, setResult] = useState(null);
  const [advanceTax, setAdvanceTax] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/tax/compare", {
        income: {
          salary_gross: Number(income.salary_gross) || 0,
          hra_received: Number(income.hra_received) || 0,
          rent_paid: Number(income.rent_paid) || 0,
          metro_city: !!income.metro_city,
          capital_gains_short: Number(income.capital_gains_short) || 0,
          capital_gains_long: Number(income.capital_gains_long) || 0,
          business_income: Number(income.business_income) || 0,
          interest_income: Number(income.interest_income) || 0,
        },
        deductions: {
          sec_80c: Number(income.sec_80c) || 0,
          sec_80d: Number(income.sec_80d) || 0,
          sec_80ccd_1b: Number(income.sec_80ccd_1b) || 0,
          home_loan_interest: Number(income.home_loan_interest) || 0,
        },
        senior,
        super_senior: superSenior,
        assessment_year: assessmentYear,
      });
      setResult(data);

      // Fetch advance tax schedule for the recommended regime
      const recommendedTax = data[data.recommended]?.total_tax || 0;
      if (recommendedTax > 10000) {
        const fy = assessmentYear === "2026-27" ? "2025-26" : "2024-25";
        const { data: at } = await api.post("/tax/advance-tax", {
          total_annual_tax: recommendedTax,
          tds_estimated: 0,
          already_paid: 0,
          financial_year: fy,
        });
        setAdvanceTax(at);
      } else {
        setAdvanceTax(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { calc(); /* eslint-disable-next-line */ }, []);

  const Wrap = user ? AppShell : ({ children }) => (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-10">{children}</div>
      <Footer />
    </div>
  );

  const chartData = result ? [
    { name: "Tax", Old: result.old.total_tax, New: result.new.total_tax },
    { name: "Taxable", Old: result.old.taxable_income, New: result.new.taxable_income },
  ] : [];

  const setVal = (k, v) => setIncome((i) => ({ ...i, [k]: v }));

  return (
    <Wrap>
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">AY {assessmentYear} · Tax Calculator</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Compare old vs new regime in real time.</h1>
          <p className="mt-3 text-muted-foreground">Enter your numbers — we'll compute both regimes, recommend the better one, and show the breakup.</p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="p-6 border-border lg:col-span-3 space-y-6" data-testid="calculator-form">
            {/* AY + senior controls */}
            <div className="flex flex-wrap gap-5 items-end">
              <div>
                <Label className="text-xs">Assessment Year</Label>
                <div className="mt-1.5">
                  <select
                    className="h-11 border border-input rounded-md px-3 bg-background text-sm"
                    value={assessmentYear}
                    onChange={(e) => setAssessmentYear(e.target.value)}
                    data-testid="calc-ay"
                  >
                    {SUPPORTED_AYS.map((ay) => (
                      <option key={ay} value={ay}>AY {ay}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={senior} onChange={(e) => { setSenior(e.target.checked); if (e.target.checked) setSuperSenior(false); }}
                  className="h-4 w-4 accent-primary" data-testid="calc-senior" />
                Senior citizen (60–79 yrs)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={superSenior} onChange={(e) => { setSuperSenior(e.target.checked); if (e.target.checked) setSenior(false); }}
                  className="h-4 w-4 accent-primary" data-testid="calc-super-senior" />
                Super senior (80+ yrs)
              </label>
            </div>

            <Section title="Income">
              <NField k="salary_gross" label="Annual gross salary" income={income} setVal={setVal} />
              <NField k="capital_gains_short" label="Short-term capital gain" income={income} setVal={setVal} />
              <NField k="capital_gains_long" label="Long-term capital gain" income={income} setVal={setVal} />
              <NField k="business_income" label="Business / freelance" income={income} setVal={setVal} />
              <NField k="interest_income" label="Interest income (FD/savings)" income={income} setVal={setVal} />
            </Section>

            <Section title="HRA & House">
              <NField k="hra_received" label="HRA received" income={income} setVal={setVal} />
              <NField k="rent_paid" label="Rent paid annually" income={income} setVal={setVal} />
              <Field label="City">
                <select className="h-11 w-full border border-input rounded-md px-3 bg-background" value={income.metro_city ? "metro" : "non_metro"} onChange={(e) => setVal("metro_city", e.target.value === "metro")}>
                  <option value="metro">Metro</option>
                  <option value="non_metro">Non-metro</option>
                </select>
              </Field>
              <NField k="home_loan_interest" label="Home loan interest" income={income} setVal={setVal} />
            </Section>

            <Section title="Deductions (old regime only)">
              <NField k="sec_80c" label="80C (max ₹1,50,000)" income={income} setVal={setVal} />
              <NField k="sec_80ccd_1b" label="80CCD(1B) NPS (max ₹50,000)" income={income} setVal={setVal} />
              <NField k="sec_80d" label="80D Health insurance" income={income} setVal={setVal} />
            </Section>

            <Button onClick={calc} disabled={loading} className="w-full h-11" data-testid="calc-btn">
              {loading ? "Calculating…" : <>Calculate tax <ChevronRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </Card>

          <div className="lg:col-span-2 space-y-5">
            {result && (
              <>
                <Card className={`p-6 border-2 ${result.recommended === "new" ? "border-accent" : "border-primary"}`} data-testid="recommendation-card">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent" />
                    <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">Recommended</div>
                  </div>
                  <div className="font-display text-2xl font-bold mt-2 capitalize">{result.recommended} regime</div>
                  <div className="mt-3 num-rupee font-display text-4xl font-bold">
                    {inr(result[result.recommended].total_tax)}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    You save <span className="font-semibold text-accent num-rupee">{inr(result.savings)}</span> vs the other regime.
                  </div>
                </Card>

                <Card className="p-5 border-border" data-testid="result-old">
                  <div className="flex items-center justify-between">
                    <div className="font-display font-semibold">Old regime</div>
                    {result.recommended !== "old" && <TrendingDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="mt-2 num-rupee font-display text-2xl font-bold">{inr(result.old.total_tax)}</div>
                  <div className="mt-3 text-xs text-muted-foreground grid grid-cols-2 gap-2">
                    <div>Taxable: <span className="num-rupee text-foreground">{inr(result.old.taxable_income)}</span></div>
                    <div>87A: <span className="num-rupee text-foreground">{inr(result.old.rebate_87a)}</span></div>
                    <div>Eff. rate: <span className="text-foreground">{result.old.effective_rate}%</span></div>
                    <div>HRA exempt: <span className="num-rupee text-foreground">{inr(result.old.hra_exemption)}</span></div>
                  </div>
                </Card>

                <Card className="p-5 border-border" data-testid="result-new">
                  <div className="flex items-center justify-between">
                    <div className="font-display font-semibold">New regime</div>
                    {result.recommended !== "new" && <TrendingDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="mt-2 num-rupee font-display text-2xl font-bold">{inr(result.new.total_tax)}</div>
                  <div className="mt-3 text-xs text-muted-foreground grid grid-cols-2 gap-2">
                    <div>Taxable: <span className="num-rupee text-foreground">{inr(result.new.taxable_income)}</span></div>
                    <div>87A: <span className="num-rupee text-foreground">{inr(result.new.rebate_87a)}</span></div>
                    <div>Eff. rate: <span className="text-foreground">{result.new.effective_rate}%</span></div>
                    <div>Std. ded.: <span className="num-rupee text-foreground">{inr(result.new.standard_deduction)}</span></div>
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>

        {result && (
          <Card className="mt-6 p-6 border-border">
            <div className="font-display text-lg font-semibold">Visual comparison</div>
            <div className="mt-4 h-72">
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    formatter={(v) => inr(v)}
                  />
                  <Bar dataKey="Old" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="New" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {result.assessment_year && (
              <div className="mt-3 text-xs text-muted-foreground">
                Computed under <span className="font-medium">{result.act || "ITA 1961"}</span> for AY {result.assessment_year}
              </div>
            )}
          </Card>
        )}

        {/* Advance Tax Schedule (Section 211) */}
        {advanceTax && advanceTax.required && (
          <Card className="mt-6 p-6 border-border">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-accent mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-display text-lg font-semibold">Advance Tax Schedule · Section 211</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Your estimated tax exceeds ₹10,000 — advance tax instalments are required for FY {advanceTax.financial_year}.
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(advanceTax.schedule || []).map((q) => (
                <Card key={q.quarter} className="p-4 border-border bg-muted/30">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{q.quarter}</div>
                  <div className="mt-1 text-xs text-muted-foreground">Due {q.due_date}</div>
                  <div className="mt-2 num-rupee font-display text-xl font-bold">{inr(q.instalment)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{q.cumulative_pct}% cumulative</div>
                </Card>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Instalments are based on the {result.recommended} regime recommendation. Paying late attracts interest u/s 234C.
            </div>
          </Card>
        )}
      </div>
    </Wrap>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">{title}</div>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const NField = ({ k, label, income, setVal }) => (
  <Field label={label}>
    <Input
      type="number"
      inputMode="numeric"
      value={income[k] ?? ""}
      onChange={(e) => setVal(k, e.target.value === "" ? 0 : Number(e.target.value))}
      className="h-11 num-rupee"
      data-testid={`calc-${k}`}
    />
  </Field>
);
